import React from "react"

import { Button } from "@/components/ui/button"
import { EditIcon, type EditIconHandle } from "@/components/ui/edit"
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field"
import { TrashIcon, type TrashIconHandle } from "@/components/ui/trash"
import { cn } from "@/lib/utils"

import {
  DASHBOARD_LAYOUT_FIELD_DESC_CLASS,
  DASHBOARD_LAYOUT_FIELD_LABEL_CLASS,
} from "./dashboardLayoutUi"
import { dashboardMutedGrayTextClassName } from "./dashboardChartCardShell"

export interface DashboardEditLayoutToolbarProps {
  layoutName: string
  showRenameAndDelete: boolean
  canDelete: boolean
  onRename: () => void
  onDelete: () => void
  onCancel: () => void
  onSave: () => void
}

const DashboardEditLayoutToolbar: React.FC<DashboardEditLayoutToolbarProps> = ({
  layoutName,
  showRenameAndDelete,
  canDelete,
  onRename,
  onDelete,
  onCancel,
  onSave,
}) => {
  const editIconRef = React.useRef<EditIconHandle | null>(null)
  const trashIconRef = React.useRef<TrashIconHandle | null>(null)

  const startEditIconAnimation = React.useCallback(() => {
    void editIconRef.current?.startAnimation()
  }, [])

  const stopEditIconAnimation = React.useCallback(() => {
    void editIconRef.current?.stopAnimation()
  }, [])

  const startTrashIconAnimation = React.useCallback(() => {
    void trashIconRef.current?.startAnimation()
  }, [])

  const stopTrashIconAnimation = React.useCallback(() => {
    void trashIconRef.current?.stopAnimation()
  }, [])

  const editIconHoverHandlers = React.useMemo(
    () => ({
      onPointerDownCapture: startEditIconAnimation,
      onMouseEnter: startEditIconAnimation,
      onMouseLeave: stopEditIconAnimation,
    }),
    [startEditIconAnimation, stopEditIconAnimation]
  )

  const trashIconHoverHandlers = React.useMemo(
    () => ({
      onPointerDownCapture: startTrashIconAnimation,
      onMouseEnter: startTrashIconAnimation,
      onMouseLeave: stopTrashIconAnimation,
    }),
    [startTrashIconAnimation, stopTrashIconAnimation]
  )

  return (
    <div data-dashboard-edit-layout-toolbar="">
      <Field className="min-w-0 flex-1 gap-1">
        <FieldLabel className={DASHBOARD_LAYOUT_FIELD_LABEL_CLASS}>Edit layout mode</FieldLabel>
        <FieldDescription className={DASHBOARD_LAYOUT_FIELD_DESC_CLASS}>
          Editing: {layoutName}. Click a card to change or remove it (top row always has four metrics).
          Drag the handle to reorder, or use the arrows on mobile and tablet. Click empty slots to add hidden cards back.
        </FieldDescription>
      </Field>

      <div
        data-dashboard-edit-layout-toolbar-actions=""
        data-slot="sidebar-inset-header-actions"
        className="flex shrink-0 flex-nowrap items-center gap-3"
      >
        {showRenameAndDelete ? (
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-font="primary"
              data-reports-rename-button=""
              className="shrink-0"
              onClick={onRename}
              {...editIconHoverHandlers}
            >
              <EditIcon
                ref={editIconRef}
                data-icon="inline-start"
                size={16}
                className={cn("shrink-0", dashboardMutedGrayTextClassName)}
                aria-hidden
              />
              <span data-font="primary" className={dashboardMutedGrayTextClassName}>
                Rename
              </span>
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-font="primary"
              data-trade-log-action="delete"
              className="shrink-0 border-destructive/40 text-destructive shadow-sm hover:bg-destructive/10 hover:text-destructive focus-visible:border-destructive/60 focus-visible:ring-destructive/25 dark:border-destructive/50 dark:hover:bg-destructive/15 [&_svg]:text-current"
              disabled={!canDelete}
              onClick={onDelete}
              {...trashIconHoverHandlers}
            >
              <TrashIcon
                ref={trashIconRef}
                data-icon="inline-start"
                size={16}
                className="shrink-0 text-current"
                aria-hidden
              />
              <span data-font="primary">Delete</span>
            </Button>
          </>
        ) : null}
        <button type="button" data-strategy-pine-detail-cancel="" data-font="primary" onClick={onCancel}>
          Cancel
        </button>
        <Button
          type="button"
          variant="gradient"
          size="sm"
          data-font="primary"
          className="shrink-0"
          onClick={onSave}
        >
          <span data-font="primary">Save</span>
        </Button>
      </div>
    </div>
  )
}

export default DashboardEditLayoutToolbar
